import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ReadOnlyBanner } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Upload, Sparkles, FileText } from "lucide-react";
import { useTranslation } from "react-i18next";

function Sources({ list }) {
  const { t } = useTranslation();
  if (!list?.length) return null;
  return (
    <div className="mt-4 border-t border-slate-100 pt-3">
      <p className="font-mono text-[11px] uppercase text-slatebody mb-1.5">{t("context.sourcesUsed")}</p>
      <ul className="space-y-1">
        {list.map((s, i) => (
          <li key={i} className="font-mono text-[11px] text-slatebody flex items-center gap-1.5">
            <FileText className="h-3 w-3" /> {s.kind} · {s.name} · {new Date(s.added_at).toLocaleDateString()}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Ingest({ onText, disabled }) {
  const { t } = useTranslation();
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await api.post("/context/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onText(res.data.text, "upload", file.name);
      toast.success(t("context.fileImported"));
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="flex flex-col sm:flex-row gap-2 mb-4">
      <input ref={fileRef} type="file" accept=".txt,.pdf,.docx,.xlsx,.csv,.md" onChange={upload} className="hidden" data-testid="ingest-file-input" />
      <Button type="button" variant="outline" size="sm" disabled={disabled || busy} onClick={() => fileRef.current?.click()} className="gap-1.5" data-testid="ingest-upload">
        <Upload className="h-3.5 w-3.5" /> {t("context.uploadFile")}
      </Button>
    </div>
  );
}

export default function ContextPlan() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const readOnly = user?.subscription_status === "read_only";
  const [data, setData] = useState(null);
  const [orgText, setOrgText] = useState("");
  const [orgSourceKind, setOrgSourceKind] = useState("paste");
  const [orgSourceName, setOrgSourceName] = useState("manual entry");
  const [scopeText, setScopeText] = useState("");
  const [shareScope, setShareScope] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const res = await api.get("/context");
    setData(res.data);
    setOrgText(res.data.org_context?.content || "");
    setScopeText(res.data.scope?.content || "");
    setShareScope(res.data.share_scope_in_compile !== false);
  };
  useEffect(() => { load(); }, []);

  const toggleScopeSharing = async (val) => {
    setShareScope(val);
    try { await api.put("/access/scope-sharing", { allow: val }); toast.success(val ? t("context.scopeShareOn") : t("context.scopeShareOff")); }
    catch (err) { setShareScope(!val); toast.error(apiError(err)); }
  };

  const saveOrg = async () => {
    setBusy(true);
    try {
      await api.put("/context/org", { content: orgText, source_kind: orgSourceKind, source_name: orgSourceName });
      toast.success(t("context.orgSaved"));
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const organize = async () => {
    if (!orgText.trim()) { toast.error(t("context.addContentFirst")); return; }
    setBusy(true);
    try {
      const res = await api.post("/context/organize", { text: orgText });
      setOrgText(res.data.content);
      toast.success(t("context.organizedAI"));
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const saveScope = async () => {
    setBusy(true);
    try {
      await api.put("/context/scope", { content: scopeText });
      toast.success(t("context.scopeSaved"));
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  if (!data) return <p className="text-slatebody">{t("common.loading")}</p>;

  return (
    <div className="animate-fade-up">
      <ReadOnlyBanner />
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold text-navy">{t("context.title")}</h1>
        <p className="text-slatebody mt-1 max-w-2xl">{t("context.subtitle")}</p>
        <p className="text-sm text-slatebody mt-3 max-w-2xl leading-relaxed" data-testid="context-intro-msg">{t("context.introMsg")}</p>
      </div>

      <Tabs defaultValue="org">
        <TabsList className="mb-6 max-w-full overflow-x-auto justify-start" data-testid="context-tabs">
          <TabsTrigger value="org" data-testid="tab-org">{t("context.tabOrg")}</TabsTrigger>
          <TabsTrigger value="scope" data-testid="tab-scope">{t("context.tabScope")}</TabsTrigger>
        </TabsList>

        <TabsContent value="org">
          <div className="rounded-xl border border-slate-200 bg-white p-6 md:p-8">
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-display text-lg font-semibold text-navy">{t("context.tabOrg")}</h2>
            </div>
            <p className="text-sm text-slatebody mb-5">{t("context.orgDesc")}</p>

            <Ingest disabled={readOnly} onText={(txt, kind, name) => { setOrgText((prev) => prev ? prev + "\n\n" + txt : txt); setOrgSourceKind(kind); setOrgSourceName(name); }} />
            <Textarea value={orgText} onChange={(e) => { setOrgText(e.target.value); setOrgSourceKind("paste"); setOrgSourceName("manual entry"); }} disabled={readOnly}
              rows={10} placeholder={t("context.orgPlaceholder")} data-testid="org-textarea" />
            <div className="flex gap-2 mt-4">
              <Button className="bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0]" disabled={busy || readOnly} onClick={saveOrg} data-testid="org-save">{t("common.save")}</Button>
              <Button variant="outline" disabled={busy || readOnly} onClick={organize} className="gap-1.5" data-testid="org-organize"><Sparkles className="h-4 w-4" /> {t("context.organizeAI")}</Button>
            </div>
            <Sources list={data.org_context?.sources} />
          </div>
        </TabsContent>

        <TabsContent value="scope">
          <div className="rounded-xl border border-slate-200 bg-white p-6 md:p-8">
            <h2 className="font-display text-lg font-semibold text-navy mb-1">{t("context.scopeTitle")}</h2>
            <p className="text-sm text-slatebody mb-5">{t("context.scopeDesc")}</p>
            <Ingest disabled={readOnly} onText={(txt) => setScopeText((prev) => prev ? prev + "\n\n" + txt : txt)} />
            <Textarea value={scopeText} onChange={(e) => setScopeText(e.target.value)} disabled={readOnly} rows={8} placeholder={t("context.scopePlaceholder")} data-testid="scope-textarea" />
            <div className="mt-4">
              <Button className="bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0]" disabled={busy || readOnly} onClick={saveScope} data-testid="scope-save">{t("common.save")}</Button>
            </div>
            {user?.account_type === "organization" && (
              <div className="mt-5 flex items-center justify-between gap-4 rounded-lg border border-slate-100 bg-background p-4" data-testid="scope-sharing-panel">
                <div className="min-w-0">
                  <p className="font-medium text-navy text-sm">{t("context.scopeShareTitle")}</p>
                  <p className="text-xs text-slatebody">{t("context.scopeShareHint")}</p>
                </div>
                <Switch checked={shareScope} onCheckedChange={toggleScopeSharing} disabled={readOnly} data-testid="scope-sharing-toggle" />
              </div>
            )}
            <Sources list={data.scope?.sources} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
